import { NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { TimePipe } from '@common/pipes/time.pipe';
import { environment } from '@environments/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { TimePickerComponent, TimePickerCustomMessagesComponent } from '@progress/kendo-angular-dateinputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BaseInputComponent } from './base-input/base-input.component';
import { DateTimeInput } from './datetime-input/datetime-input.component';
import { InputErrorComponent } from './input-error/input-error.component';
import { InputLabelComponent } from './input-label/input-label.component';
import { TimeInputConfig } from './input.type';

@Component({
    selector: 'app-time-input',
    imports: [
        InputLabelComponent,
        InputErrorComponent,
        TimePickerComponent,
        TimePickerCustomMessagesComponent,
        TimePipe,
        NgClass,
        TranslatePipe,
        FormsModule,
        TooltipDirective
    ],
    template: `
        <app-input-label
            [label]="label"
            [labelPosition]="labelPosition"
            [for]="id"
            [isRequired]="isRequired"
            [isEditMode]="isEditMode">
            @if (isEditMode) {
                <kendo-timepicker
                    class="bg-input text-input-foreground"
                    [ngClass]="generateInputStyle()"
                    [id]="id"
                    [adaptiveTitle]="title | translate"
                    [placeholder]="placeholder"
                    [format]="effectiveFormat()"
                    [formatPlaceholder]="formatPlaceholder()"
                    [required]="isRequired"
                    [max]="maxTime()"
                    [min]="minTime()"
                    [disabled]="isDisabled"
                    [steps]="steps()"
                    [ngModel]="timeValue()"
                    (valueChange)="onValueChange($event)"
                    (focusin)="onFocusIn()"
                    (focusout)="onFocusOut()">
                    <kendo-timepicker-messages
                        [now]="'Now' | translate"
                        [accept]="'Accept' | translate"
                        [cancel]="'Cancel' | translate"
                        [hour]="'Hour' | translate"
                        [minute]="'Minute' | translate"
                        [second]="'Second' | translate" />
                </kendo-timepicker>
                <app-input-error [error]="error()?.[0]" />
            } @else {
                <div class="w-full truncate pt-1.5 pb-1">
                    <span kendoTooltip [title]="timeValue() | time">
                        {{ timeValue() | time }}
                    </span>
                </div>
            }
        </app-input-label>
    `,
    providers: [{ provide: BaseInputComponent, useExisting: TimeInputComponent }]
})
export class TimeInputComponent extends BaseInputComponent<DateTimeInput> {
    format = input<TimeInputConfig['format']>();
    formatPlaceholder = input<TimeInputConfig['formatPlaceholder']>({
        hour: this.translateService.instant('hour'),
        minute: this.translateService.instant('minute'),
        second: this.translateService.instant('second'),
        millisecond: this.translateService.instant('millisecond')
    });
    minTime = input<TimeInputConfig['minTime']>();
    maxTime = input<TimeInputConfig['maxTime']>();
    steps = input<TimeInputConfig['steps']>();

    defaultTimeFormat: string = environment.settings.input.timeFormat;

    timeValue = computed<Date>(() => {
        if (!this.value()) return null;
        if (this.value() instanceof Date) {
            return this.value() as Date;
        }
        const date = new Date(this.value());
        return isNaN(date.getTime()) ? null : date;
    });

    effectiveFormat = computed(() => this.format() || this.defaultTimeFormat);

    constructor() {
        super();
        this.getDefaultDateFormat();
    }

    override onValueChange(value: DateTimeInput) {
        this.value.set(value);
        super.onValueChange(value);
    }

    private getDefaultDateFormat() {
        const settings = this.queryService.queryClient.getQueryData<Setting[]>(['settings']);
        if (!settings) return;
        const defaultTimeFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.TimeFormat
        )?.value;
        if (defaultTimeFormat) this.defaultTimeFormat = defaultTimeFormat;
    }
}
